import React, { useEffect, useRef, useState } from 'react'
import styled, { keyframes } from 'styled-components'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import CloseRoundedIcon       from '@mui/icons-material/CloseRounded'
import { fetchComplianceHints } from 'services/ComplianceHintService'

/**
 * Debounced AI hint surface used at every input moment (Action create,
 * Task create, library pick, file upload). Renders 0-3 soft badges below
 * the input the user is currently editing. Each badge is dismissible and
 * one-click "Apply" to inject its payload into the parent form.
 *
 * Props:
 *   context: 'action_create' | 'task_create' | 'library_pick' | 'file_upload'
 *   buildingPassportId: <uuid> (optional)
 *   payload: { title, description, fileTypeId, ... } — whatever the user has typed so far
 *   onApply: (hint) => void
 */
const HintBadge = ({ context, buildingPassportId, payload, onApply, debounceMs = 350 }) => {
  const [hints, setHints] = useState([])
  const [dismissed, setDismissed] = useState(() => new Set())
  const [loading, setLoading] = useState(false)
  const timer = useRef(null)
  const reqRef = useRef(0)

  const inputSig = JSON.stringify(payload || {})

  useEffect(() => {
    const text = (payload?.title || '') + (payload?.description || '')
    if (text.trim().length < 4) { setHints([]); return }

    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => {
      const seq = ++reqRef.current
      setLoading(true)
      fetchComplianceHints({ context, buildingPassportId, payload })
        .then(res => {
          if (seq !== reqRef.current) return
          setHints(res.data?.data?.hints || [])
        })
        .catch(() => { if (seq === reqRef.current) setHints([]) })
        .finally(() => { if (seq === reqRef.current) setLoading(false) })
    }, debounceMs)

    return () => { if (timer.current) clearTimeout(timer.current) }
  }, [context, buildingPassportId, inputSig, debounceMs, payload])

  const visible = hints.filter(h => !dismissed.has(h.id))
  if (!visible.length && !loading) return null

  return (
    <Wrap>
      <Eyebrow>
        <AutoAwesomeRoundedIcon style={{ fontSize: 14 }} />
        <span>{loading ? 'Thinking…' : 'AI suggestions'}</span>
      </Eyebrow>
      <Badges>
        {visible.map(h => (
          <Badge key={h.id} $confidence={h.confidence}>
            <KindDot $kind={h.kind} />
            <BadgeLabel>{h.label}</BadgeLabel>
            <Apply onClick={() => onApply?.(h)} title='Apply this suggestion'>Apply</Apply>
            <Dismiss
              onClick={() => setDismissed(prev => new Set(prev).add(h.id))}
              title='Dismiss'
            >
              <CloseRoundedIcon style={{ fontSize: 11 }} />
            </Dismiss>
          </Badge>
        ))}
      </Badges>
    </Wrap>
  )
}

export default HintBadge

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-3px); }
  to   { opacity: 1; transform: translateY(0); }
`

const KIND_COLOURS = {
  classify:        '#33becc',
  link_obligation: '#33becc',
  satisfies:       '#2aa6b3',
  severity:        '#d97706',
  domain:          '#7C5FE0',
}

const Wrap = styled.div`
  margin-top: 6px;
  animation: ${fadeIn} 200ms ease both;
`

const Eyebrow = styled.div`
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 10px;
  letter-spacing: 0.10em;
  text-transform: uppercase;
  color: #1f8c97;
  font-weight: 800;
  margin-bottom: 6px;
`

const Badges = styled.div`
  display: flex; flex-direction: column; gap: 6px;
`

const Badge = styled.div`
  display: inline-flex; align-items: center; gap: 8px;
  padding: 6px 6px 6px 10px;
  background: rgba(51, 190, 204, 0.06);
  border: 1px solid rgba(51, 190, 204, 0.24);
  border-radius: 999px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.haiti};
  max-width: 100%;
  opacity: ${({ $confidence }) => $confidence === 'low' ? 0.85 : 1};
  animation: ${fadeIn} 220ms ease both;
`

const KindDot = styled.span`
  width: 8px; height: 8px; border-radius: 50%;
  background: ${({ $kind }) => KIND_COLOURS[$kind] || '#33becc'};
  flex-shrink: 0;
`

const BadgeLabel = styled.span`
  flex: 1; min-width: 0;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
`

const Apply = styled.button`
  font: inherit;
  background: ${({ theme }) => theme.colors.secondary};
  border: none;
  color: #fff;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  padding: 4px 10px;
  border-radius: 999px;
  cursor: pointer;
  &:hover { filter: brightness(1.08); }
`

const Dismiss = styled.button`
  display: inline-flex; align-items: center; justify-content: center;
  width: 20px; height: 20px;
  background: rgba(12,18,41,0.06);
  border: none; border-radius: 50%;
  color: #4d4d56;
  cursor: pointer;
  &:hover { background: rgba(12,18,41,0.14); color: #0C1229; }
`
